import { useEffect, useMemo, useState } from "react";
import client from "../api/client";
import { getNextVoucherNo } from "../services/voucherNumberService";

const today = () => new Date().toISOString().split("T")[0];

const REFERENCE_TYPES = [
  {
    value: "ON_ACCOUNT",
    label: "On A/C",
    help: "General receipt without invoice adjustment",
  },
  {
    value: "TOWARDS_BILL",
    label: "Against Bill / Reference",
    help: "Customer payment against invoice/reference",
  },
  {
    value: "ADVANCE",
    label: "Advance",
    help: "Advance received before invoice",
  },
  {
    value: "NEW_BILL",
    label: "New Bill",
    help: "Receipt linked to a new manual bill reference",
  },
];

const initialForm = {
  voucherNo: "",
  voucherDate: today(),
  receiptMode: "CASH",
  referenceType: "ON_ACCOUNT",
  billReferenceNo: "",
  customerId: "",
  debitAccountId: "",
  creditAccountId: "",
  amount: "",
  notes: "",
};

const getId = (x) => x?.Id ?? x?.id ?? x?.AccountId ?? x?.accountId ?? "";
const getAccountCode = (x) => x?.AccountCode ?? x?.accountCode ?? "";
const getAccountName = (x) => x?.AccountName ?? x?.accountName ?? "";
const getAccountType = (x) =>
  (x?.AccountType ?? x?.accountType ?? "").toString();
const getNormal = (x) =>
  (
    x?.NormalBalance ??
    x?.normalBalance ??
    x?.NormalSide ??
    x?.normalSide ??
    x?.Normal ??
    x?.normal ??
    ""
  ).toString();
const isGstLedger = (x) =>
  /gst|cgst|sgst|igst/i.test(`${getAccountCode(x)} ${getAccountName(x)}`);
const isCashBankLedger = (x) => {
  const name = getAccountName(x).toLowerCase();
  const code = getAccountCode(x);
  return (
    name.includes("cash") ||
    name.includes("bank") ||
    name.includes("upi") ||
    code === "1100" ||
    code === "1200"
  );
};
const isReceivableLedger = (x) => {
  const name = getAccountName(x).toLowerCase();
  const code = getAccountCode(x);
  return (
    code === "1300" ||
    name.includes("receivable") ||
    name.includes("debtor") ||
    name.includes("sundry debtor") ||
    name.includes("customer")
  );
};
const isIncomeLedger = (x) => {
  const name = getAccountName(x).toLowerCase();
  const type = getAccountType(x).toLowerCase();
  return (
    type === "income" ||
    name.includes("income") ||
    name.includes("sales") ||
    name.includes("service revenue")
  );
};
const isAdvanceOrLiabilityLedger = (x) => {
  const name = getAccountName(x).toLowerCase();
  const type = getAccountType(x).toLowerCase();
  return (
    type === "liability" ||
    type === "equity" ||
    name.includes("advance") ||
    name.includes("capital") ||
    name.includes("loan")
  );
};
const getCustomerId = (x) =>
  x?.Id ?? x?.id ?? x?.CustomerId ?? x?.customerId ?? "";
const getCustomerName = (x) =>
  x?.CustomerName ?? x?.customerName ?? x?.Name ?? x?.name ?? "";
const getCustomerCode = (x) =>
  x?.CustomerCode ?? x?.customerCode ?? x?.Code ?? x?.code ?? "";

const money = (v) =>
  Number(v || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

export default function ReceiptVoucherPage() {
  const [form, setForm] = useState(initialForm);
  const [accounts, setAccounts] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [saving, setSaving] = useState(false);
  const [autoNarration, setAutoNarration] = useState(true);

  const findAccount = (mode, list = accounts) => {
    const m = (mode || "").toLowerCase();
    if (m === "cash")
      return list.find(
        (x) =>
          getAccountName(x).toLowerCase().includes("cash") ||
          getAccountCode(x) === "1100",
      );
    if (m === "bank")
      return list.find(
        (x) =>
          getAccountName(x).toLowerCase().includes("bank") ||
          getAccountCode(x) === "1200",
      );
    if (m === "upi")
      return (
        list.find((x) => getAccountName(x).toLowerCase().includes("upi")) ||
        list.find((x) => getAccountName(x).toLowerCase().includes("bank"))
      );
    return list.find((x) => getAccountType(x).toLowerCase() === "asset");
  };

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    const shortcut = (e) => {
      if (e.ctrlKey && e.key.toLowerCase() === "s") {
        e.preventDefault();
        save();
      }
      if (e.altKey && e.key.toLowerCase() === "c") {
        e.preventDefault();
        clearForm();
      }
    };
    window.addEventListener("keydown", shortcut);
    return () => window.removeEventListener("keydown", shortcut);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form, accounts]);

  const accountMap = useMemo(() => {
    const map = {};
    accounts.forEach((a) => {
      map[String(getId(a))] = `${getAccountCode(a)} - ${getAccountName(a)}`;
    });
    return map;
  }, [accounts]);

  const receiptDebitAccounts = useMemo(
    () =>
      accounts.filter((a) => {
        const name = getAccountName(a).toLowerCase();
        const code = getAccountCode(a);
        const type = getAccountType(a).toLowerCase();
        return isCashBankLedger(a);
      }),
    [accounts],
  );

  const receiptCreditAccounts = useMemo(
    () =>
      accounts.filter((a) => {
        // Production filter: avoid confusion by hiding GST, Cash/Bank and other wrong ledgers.
        // Customer payment against invoice => Accounts Receivable / Customer ledger only.
        if (isGstLedger(a) || isCashBankLedger(a)) return false;
        if (form.referenceType === "TOWARDS_BILL") return isReceivableLedger(a);
        if (form.referenceType === "ADVANCE")
          return isReceivableLedger(a) || isAdvanceOrLiabilityLedger(a);
        if (form.referenceType === "NEW_BILL")
          return isReceivableLedger(a) || isIncomeLedger(a);
        return (
          isReceivableLedger(a) ||
          isIncomeLedger(a) ||
          isAdvanceOrLiabilityLedger(a)
        );
      }),
    [accounts, form.referenceType],
  );

  const selectedCustomer = useMemo(
    () =>
      customers.find(
        (c) => String(getCustomerId(c)) === String(form.customerId),
      ),
    [customers, form.customerId],
  );

  const recommendedCreditAccountId = useMemo(() => {
    const exactCustomer = selectedCustomer
      ? receiptCreditAccounts.find((a) =>
          getAccountName(a)
            .toLowerCase()
            .includes(getCustomerName(selectedCustomer).toLowerCase()),
        )
      : null;
    const receivable = receiptCreditAccounts.find(
      (a) =>
        getAccountCode(a) === "1300" ||
        getAccountName(a).toLowerCase().includes("receivable"),
    );
    const income = receiptCreditAccounts.find(
      (a) =>
        getAccountType(a).toLowerCase() === "income" ||
        getAccountName(a).toLowerCase().includes("sales"),
    );
    return (
      getId(
        exactCustomer || receivable || income || receiptCreditAccounts[0],
      ) || ""
    );
  }, [receiptCreditAccounts, selectedCustomer]);

  useEffect(() => {
    if (
      !form.creditAccountId ||
      !receiptCreditAccounts.some(
        (a) => String(getId(a)) === String(form.creditAccountId),
      )
    ) {
      if (recommendedCreditAccountId)
        setForm((prev) => ({
          ...prev,
          creditAccountId: recommendedCreditAccountId,
        }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.referenceType, form.customerId, recommendedCreditAccountId]);

  const selectedCreditHint =
    form.referenceType === "TOWARDS_BILL"
      ? "Select 1300 - Accounts Receivable A/c or exact Customer ledger. Do not select GST Output."
      : form.referenceType === "ADVANCE"
        ? "Select Customer Advance / Liability or Customer ledger."
        : "Select Customer / Income / Liability ledger. Cash/Bank and GST ledgers are hidden.";

  const debitName =
    accountMap[String(form.debitAccountId)] || "Cash / Bank A/c";
  const creditName =
    accountMap[String(form.creditAccountId)] || "Customer / Income A/c";

  const generateVoucherNo = () => localFallbackVoucherNo("RCPT");

  const localFallbackVoucherNo = (prefix) => {
    const d = new Date();
    return `${prefix}-${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, "0")}${String(d.getDate()).padStart(2, "0")}-${String(d.getHours()).padStart(2, "0")}${String(d.getMinutes()).padStart(2, "0")}${String(d.getSeconds()).padStart(2, "0")}`;
  };

  const refreshVoucherNo = async () => {
    const no = await getNextVoucherNo("receipt", form.voucherDate);
    setForm((prev) => ({ ...prev, voucherNo: no }));
    return no;
  };

  const buildNarration = (f = form) => {
    const refLabel =
      REFERENCE_TYPES.find((x) => x.value === f.referenceType)?.label ||
      "On A/C";
    const party = selectedCustomer
      ? getCustomerName(selectedCustomer)
      : "party";
    const ref = f.billReferenceNo?.trim()
      ? `, Ref: ${f.billReferenceNo.trim()}`
      : "";
    return `Being amount received from ${party} by ${f.receiptMode}${ref} (${refLabel})`;
  };

  const loadData = async () => {
    try {
      const [accRes, custRes] = await Promise.all([
        client.get("/accounts"),
        client.get("/sales/customers"),
      ]);
      const acc = Array.isArray(accRes.data) ? accRes.data : [];
      const cust = Array.isArray(custRes.data) ? custRes.data : [];
      const cash = findAccount("CASH", acc);

      setAccounts(acc);
      setCustomers(cust);
      const nextNo = await getNextVoucherNo("receipt", today());
      setForm((prev) => ({
        ...prev,
        voucherNo: prev.voucherNo || nextNo,
        debitAccountId: getId(cash) || "",
      }));
    } catch (err) {
      console.error(err);
      alert("Failed to load accounts/customers");
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "receiptMode") {
      const selectedAccount = findAccount(value);
      setForm((prev) => ({
        ...prev,
        receiptMode: value,
        debitAccountId: getId(selectedAccount) || "",
        notes: autoNarration ? "" : prev.notes,
      }));
      return;
    }

    if (name === "referenceType") {
      setForm((prev) => ({
        ...prev,
        referenceType: value,
        billReferenceNo:
          value === "ON_ACCOUNT" || value === "ADVANCE"
            ? ""
            : prev.billReferenceNo,
        notes: autoNarration ? "" : prev.notes,
      }));
      return;
    }

    setForm((prev) => ({
      ...prev,
      [name]: value,
      notes:
        autoNarration && name !== "notes"
          ? ""
          : name === "notes"
            ? value
            : prev.notes,
    }));
  };

  const clearForm = async () => {
    const defaultAccount = findAccount(form.receiptMode);
    const nextNo = await getNextVoucherNo("receipt", today());
    setForm({
      ...initialForm,
      voucherNo: nextNo,
      voucherDate: today(),
      receiptMode: form.receiptMode,
      debitAccountId: getId(defaultAccount) || "",
    });
  };

  const validationErrors = useMemo(() => {
    const errors = [];
    if (!form.voucherNo.trim()) errors.push("Voucher No is required");
    if (!form.debitAccountId) errors.push("Select Debit Account");
    if (!form.creditAccountId) errors.push("Select Credit Account");
    if (
      String(form.debitAccountId) === String(form.creditAccountId) &&
      form.debitAccountId
    )
      errors.push("Debit and Credit account cannot be same");
    if (
      (form.referenceType === "NEW_BILL" ||
        form.referenceType === "TOWARDS_BILL") &&
      !form.billReferenceNo.trim()
    )
      errors.push("Bill / Reference No is required");
    if (!form.amount || Number(form.amount) <= 0)
      errors.push("Enter valid amount");
    return errors;
  }, [form]);

  const save = async () => {
    if (validationErrors.length) return alert(validationErrors[0]);

    try {
      setSaving(true);
      await client.post("/vouchers/receipt", {
        companyId: Number(localStorage.getItem("companyId") || 0),
        voucherNo: form.voucherNo.trim(),
        voucherDate: form.voucherDate,
        receiptMode: form.receiptMode,
        referenceType: form.referenceType,
        billReferenceNo: form.billReferenceNo.trim() || null,
        customerId: form.customerId ? Number(form.customerId) : null,
        debitAccountId: Number(form.debitAccountId),
        creditAccountId: Number(form.creditAccountId),
        amount: Number(form.amount),
        notes: (form.notes || buildNarration()).trim(),
      });

      alert("Receipt saved and journal posted");
      await clearForm();
    } catch (err) {
      console.error("Receipt Save Error:", err);
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.title ||
        err?.response?.data ||
        err?.message ||
        "Failed to save receipt";
      alert(msg);
    } finally {
      setSaving(false);
    }
  };

  const labelStyle = {
    fontSize: "12px",
    fontWeight: "600",
    marginBottom: "3px",
  };
  const inputStyle = { height: "30px", fontSize: "12px", borderRadius: "6px" };
  const buttonStyle = {
    border: "none",
    padding: "5px 14px",
    fontSize: "12px",
    borderRadius: "6px",
  };
  const effectiveNotes = form.notes || buildNarration();

  return (
    <div style={{ padding: "0px", fontSize: "13px" }}>
      <div
        style={{
          background: "#fff",
          borderRadius: "10px",
          boxShadow: "0 3px 10px rgba(0,0,0,0.08)",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            background: "linear-gradient(90deg,#198754,#20c997)",
            color: "#fff",
            padding: "10px 14px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div>
            <div style={{ fontSize: "15px", fontWeight: "700" }}>
              Receipt Voucher
            </div>
            <div style={{ fontSize: "11px", opacity: 0.9 }}>
              Cash/Bank Dr → Customer/Income/Liability Cr | Ctrl + S Save | Alt
              + C Clear
            </div>
          </div>
          <span
            style={{
              background: "#fff",
              color: "#198754",
              padding: "3px 8px",
              borderRadius: "6px",
              fontSize: "11px",
              fontWeight: "600",
            }}
          >
            Auto Journal + Locked Dr/Cr
          </span>
        </div>

        <div style={{ padding: "12px" }}>
          <div className="row g-2">
            <div className="col-md-3">
              <label style={labelStyle}>Voucher No</label>
              <input
                name="voucherNo"
                className="form-control"
                style={inputStyle}
                value={form.voucherNo}
                readOnly
              />
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm mt-1"
                style={{ fontSize: "11px", padding: "2px 8px" }}
                onClick={refreshVoucherNo}
              >
                Refresh No
              </button>
            </div>
            <div className="col-md-3">
              <label style={labelStyle}>Date</label>
              <input
                type="date"
                name="voucherDate"
                className="form-control"
                style={inputStyle}
                value={form.voucherDate}
                onChange={handleChange}
              />
            </div>
            <div className="col-md-3">
              <label style={labelStyle}>Receipt Mode</label>
              <select
                name="receiptMode"
                className="form-select"
                style={inputStyle}
                value={form.receiptMode}
                onChange={handleChange}
              >
                <option value="CASH">Cash</option>
                <option value="BANK">Bank</option>
                <option value="UPI">UPI</option>
              </select>
            </div>
            <div className="col-md-3">
              <label style={labelStyle}>Reference Type</label>
              <select
                name="referenceType"
                className="form-select"
                style={inputStyle}
                value={form.referenceType}
                onChange={handleChange}
              >
                {REFERENCE_TYPES.map((x) => (
                  <option key={x.value} value={x.value}>
                    {x.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-md-3">
              <label style={labelStyle}>Bill / Reference No</label>
              <input
                name="billReferenceNo"
                className="form-control"
                style={inputStyle}
                value={form.billReferenceNo}
                onChange={handleChange}
                disabled={
                  form.referenceType === "ON_ACCOUNT" ||
                  form.referenceType === "ADVANCE"
                }
                placeholder={
                  form.referenceType === "TOWARDS_BILL"
                    ? "Invoice / bill no"
                    : form.referenceType === "NEW_BILL"
                      ? "New bill no"
                      : "Not required"
                }
              />
            </div>
            <div className="col-md-3">
              <label style={labelStyle}>Customer</label>
              <select
                name="customerId"
                className="form-select"
                style={inputStyle}
                value={form.customerId}
                onChange={handleChange}
              >
                <option value="">Optional - Select Customer</option>
                {customers.map((c) => (
                  <option key={getCustomerId(c)} value={getCustomerId(c)}>
                    {getCustomerCode(c)} - {getCustomerName(c)}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-md-3">
              <label style={labelStyle}>Debit Account - Cash/Bank/Asset</label>
              <select
                name="debitAccountId"
                className="form-select"
                style={inputStyle}
                value={form.debitAccountId}
                onChange={handleChange}
              >
                <option value="">Select Cash / Bank / UPI</option>
                {(receiptDebitAccounts.length
                  ? receiptDebitAccounts
                  : accounts
                ).map((a) => (
                  <option key={getId(a)} value={getId(a)}>
                    {getAccountCode(a)} - {getAccountName(a)}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-md-3">
              <label style={labelStyle}>
                Credit Account - Customer/Income/Cr
              </label>
              <select
                name="creditAccountId"
                className="form-select"
                style={inputStyle}
                value={form.creditAccountId}
                onChange={handleChange}
              >
                <option value="">Select Customer / Income / Liability</option>
                {receiptCreditAccounts.map((a) => (
                  <option key={getId(a)} value={getId(a)}>
                    {getAccountCode(a)} - {getAccountName(a)}
                  </option>
                ))}
              </select>
              <div
                style={{ fontSize: "10px", color: "#198754", marginTop: "3px" }}
              >
                {selectedCreditHint}
              </div>
            </div>
            <div className="col-md-3">
              <label style={labelStyle}>Amount</label>
              <input
                type="number"
                name="amount"
                className="form-control"
                style={{ ...inputStyle, textAlign: "right" }}
                value={form.amount}
                onChange={handleChange}
              />
            </div>
            <div className="col-md-9">
              <label style={labelStyle}>Notes</label>
              <input
                name="notes"
                className="form-control"
                style={inputStyle}
                value={effectiveNotes}
                onChange={(e) => {
                  setAutoNarration(false);
                  setForm((p) => ({ ...p, notes: e.target.value }));
                }}
                placeholder="Example: Amount received against invoice / Paid supplier bill / Advance adjusted"
              />
            </div>
          </div>

          <div
            style={{
              marginTop: 8,
              display: "flex",
              gap: 14,
              alignItems: "center",
              fontSize: 11,
            }}
          >
            <label style={{ display: "flex", alignItems: "center", gap: 5 }}>
              <input
                type="checkbox"
                checked={autoNarration}
                onChange={(e) => {
                  setAutoNarration(e.target.checked);
                  if (e.target.checked) setForm((p) => ({ ...p, notes: "" }));
                }}
              />{" "}
              Auto narration
            </label>
            <span style={{ color: "#198754" }}>
              {
                REFERENCE_TYPES.find((x) => x.value === form.referenceType)
                  ?.help
              }
            </span>
          </div>

          <div
            style={{
              marginTop: "12px",
              padding: "10px",
              border: "1px dashed #b7d7c2",
              background: "#f8fffb",
              borderRadius: "8px",
              fontSize: "12px",
            }}
          >
            <div style={{ fontWeight: "700", marginBottom: "5px" }}>
              Journal Preview
            </div>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <span>{debitName} Dr</span>
              <b>₹ {money(form.amount)}</b>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <span>To {creditName}</span>
              <b>₹ {money(form.amount)}</b>
            </div>
            <div style={{ marginTop: 5, color: "#198754", fontSize: 11 }}>
              {effectiveNotes}
            </div>
          </div>

          {validationErrors.length > 0 && (
            <div style={{ marginTop: 8, color: "#dc3545", fontSize: 11 }}>
              {validationErrors[0]}
            </div>
          )}
          <div style={{ marginTop: "12px", display: "flex", gap: "8px" }}>
            <button
              onClick={save}
              disabled={saving || validationErrors.length > 0}
              style={{
                ...buttonStyle,
                background:
                  saving || validationErrors.length > 0 ? "#8abfa4" : "#198754",
                color: "#fff",
              }}
            >
              {saving ? "Saving..." : "Save"}
            </button>
            <button
              onClick={clearForm}
              type="button"
              style={{ ...buttonStyle, background: "#6c757d", color: "#fff" }}
            >
              Clear
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
